<template>
  <WdsStack direction-row gap-md class="tw-mt-4 tw-mb-4">
    <div class="tw-w-1/2">
      <WdsStack direction-column gap-md>
        <WdsSelect
          v-model="selectedLocationPreference"
          :list="locationPreferenceOptions"
          :placeholder="$t('Select location')"
          :error="errors?.locationPreference"
          name="location_preference"
          data-test="location-preference-dropdown"
        >
          <template #label>
            <WdsStack direction-row justify-space-between class="tw-w-full">
              <div>{{ $t('Primary work location') }}</div>

              <WdsTooltip>
                <template #tooltip-content>
                  {{
                    $t(
                      'For hybrid arrangements, indicate the location where the majority of work hours will be spent. If uncertain, you can update the contract after the offer is sent.',
                    )
                  }}
                </template>

                <span class="c-purple">{{ $t('What about hybrid work?') }}</span>
              </WdsTooltip>
            </WdsStack>
          </template>

          <template #option="{ option }">
            <WdsText :text="option.label.toString()" class="tw-break-normal">
              <template #subText>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="option.subLabel?.toString()" />
              </template>
            </WdsText>
          </template>
        </WdsSelect>

        <WdsAlert
          v-if="selectedLocationPreference.value === 'remote-only' && workerHasAddress"
          info-subtle
          icon
          bordered
        >
          {{ $t('Address pre-filled from Worker’s profile.') }}
          <br />
          {{ $t('Ensure this is the correct working location.') }}
        </WdsAlert>
      </WdsStack>
    </div>
    <div class="tw-w-1/2">
      <WdsStack direction-column gap-md>
        <div class="tw-w-full">
          <WdsInput
            v-model="store.address"
            :label="$t('Street')"
            :placeholder="$t('Street name')"
            name="location_address"
            :error="errors?.address"
          />
        </div>

        <WdsStack direction-row gap-sm>
          <div class="tw-w-full">
            <WdsInput
              v-model="store.city"
              :label="$t('City')"
              :placeholder="$t('City')"
              name="location_city"
              :error="errors?.city"
            />
          </div>

          <div class="tw-w-full">
            <WdsInput
              v-model="store.postCode"
              :label="postCodeLabel"
              :placeholder="postCodePlaceholder"
              name="location_zipcode"
              :error="errors?.postCode"
            />
          </div>
        </WdsStack>

        <WdsStack direction-row gap-sm>
          <div class="tw-w-full">
            <WdsSelect
              v-model="selectedCountry"
              :list="getCountries"
              :label="$t('Country')"
              :placeholder="$t('Select a country')"
              searchable
              :error="errors?.country"
              name="location_country"
            />
          </div>

          <div class="tw-w-full">
            <WdsSelect
              v-if="countryHasStates"
              v-model="selectedState"
              :list="getStates"
              :label="stateLabel"
              :placeholder="statePlaceholder"
              searchable
              :error="errors?.state"
              name="location_state"
            />
          </div>
        </WdsStack>
      </WdsStack>
    </div>
  </WdsStack>
</template>

<script setup lang="ts">
import { reactive, watch, computed, onMounted } from 'vue'
import {
  WdsAlert,
  WdsStack,
  WdsSelect,
  WdsInput,
  WdsTooltip,
  WdsText,
} from '@worksome/wds-components'

import { $t } from '@/app-bootstrapping/SetUpInternationalization'

import { useLocation } from '@/composables/useLocation'

interface FullAddress {
  address?: string | null
  postCode?: string | null
  city?: string | null
  country?: string | null
  state?: string | null
}

interface Errors {
  locationPreference?: string
  address?: string
  postCode?: string
  city?: string
  country?: string
  state?: string
}

const props = defineProps<{
  locationPreferenceOptions: Array<{ value: string; label: string }>
  locationPreference?: string | null
  address: FullAddress
  companyAddress: FullAddress
  workerAddress: FullAddress
  errors: Errors
}>()

const {
  getCountries,
  getStates,
  setSelectedCountry,
  setSelectedState,
  postCodeLabel,
  postCodePlaceholder,
  stateLabel,
  statePlaceholder,
  countryHasStates,
} = useLocation()

const store = reactive({
  locationPreference: props.locationPreference,
  address: props.address.address,
  city: props.address.city,
  postCode: props.address.postCode,
  country: props.address.country,
  state: props.address.state,
})

const preFillStoreAddressValues = (address: FullAddress) => {
  store.address = address?.address ?? ''
  store.city = address?.city ?? ''
  store.postCode = address?.postCode ?? ''
  store.country = address?.country ?? ''
  store.state = address?.state ?? ''
}

const workerHasAddress = computed(() => props.workerAddress.address !== '')

const selectedLocationPreference = computed({
  get: () => ({ value: store.locationPreference ?? '' }),
  set: (preference) => {
    store.locationPreference = preference.value
  },
})

const selectedCountry = computed({
  get: () => ({ value: store.country?.toUpperCase() ?? '' }),
  set: (country) => {
    store.country = country.value
  },
})

const selectedState = computed({
  get: () => ({ value: store.state?.toUpperCase() ?? '' }),
  set: (state) => {
    store.state = state.value
  },
})

const addressToPrefill = computed(
  (): FullAddress =>
    store.locationPreference === 'onsite-only' ? props.companyAddress : props.workerAddress,
)

onMounted(() => {
  if (Object.values(props.errors).length === 0) {
    if (store.locationPreference) {
      preFillStoreAddressValues(addressToPrefill.value)
    }
  }

  if (store.country) {
    setSelectedCountry(store.country?.toUpperCase() ?? '')
  }

  if (store.state) {
    setSelectedState(store.state?.toUpperCase() ?? '')
  }
})

watch(
  () => store.country,
  (country) => {
    setSelectedCountry(country?.toUpperCase() ?? '')
  },
)

watch(
  () => store.state,
  (state) => {
    setSelectedState(state?.toUpperCase() ?? '')
  },
)

watch(
  () => store.locationPreference,
  () => {
    preFillStoreAddressValues(addressToPrefill.value)
  },
)
</script>

<style lang="scss" scoped>
:deep(.wds-label) {
  @apply tw-w-full;
}
</style>
